import React from "react"
import { t } from "ttag"

import { APPLICATION_URL, ApplicationPath } from "features/Navigation"
import { PermissionsPage, useUserPermissions } from "features/Permissions"
import { RecipientsPage } from "features/Recipients"
import { UserGroupsPage } from "features/UserGroups"
import { QuestionsPage, SurveysPage } from "features/Surveys"
import { ScheduledSurveysPage } from "features/Surveys/ScheduledSurveys"
import { DashboardPage } from "features/Dashboard"
import { LedDiscussionsPage, LedKeyFactsPage, LedStatisticsPage } from "features/Led"
import { PimeoStatisticsPage } from "features/PimeoStatistics"
import { CustomerSession, useSessionStore } from "features/Session"
import { PackageCode, UserPermissionCode } from "models/generated"

import speakupLogo from "assets/images/speakup_logo-primary.svg"
import leaLogo from "assets/images/lea_logo.svg"

export enum FeatureWithTabs {
  Users = "users",
  SpeakUp = "speakup",
  Led = "led",
  Lea = "lea",
  PimeoStatistics = "pimeo-statistics"
}

export interface NavigationTab {
  path: string,
  title: string,
  component: React.ComponentType,
  withMenuItem?: boolean,
  isUnauthorized?: boolean,
  permissionsSet?: UserPermissionCode[],
  icon?: string,
  isIconImage?: boolean,
  // Hide the tab in the component, for example, it allow to use all ScheduledSurveys routes
  hidden?: boolean,
  // Replaces the title with another one when it is not the same as the first tab label
  forceMenuLabel?: string,
}

export const useNavigationTabs = (feature: FeatureWithTabs): NavigationTab[] => {
  const customer = useSessionStore(state => state.customer)
  const isUserAllowed = useUserPermissions()

  const navigationTabs: Record<FeatureWithTabs, NavigationTab[]> = {
    [FeatureWithTabs.Users]: [
      {
        component: RecipientsPage,
        icon: "people",
        isUnauthorized: !isUserAllowed(UserPermissionCode.RecipientsRead),
        path: ApplicationPath.Recipients,
        title: t`Users`,
        withMenuItem: true,
      },
      {
        component: PermissionsPage,
        isUnauthorized: !isUserAllowed(UserPermissionCode.PermissionsManage),
        path: ApplicationPath.Permissions,
        title: t`Administrators`,
      },
      {
        component: UserGroupsPage,
        isUnauthorized: !isUserAllowed(UserPermissionCode.UserGroupsManage),
        path: ApplicationPath.Groups,
        title: t`Groups`,
      },
    ],
    [FeatureWithTabs.SpeakUp]: getSpeakupPage(),
    [FeatureWithTabs.Led]: getLedPageForCustomer(customer),
    [FeatureWithTabs.Lea]: getLeaPage(),
    [FeatureWithTabs.PimeoStatistics]: [
      {
        component: PimeoStatisticsPage,
        forceMenuLabel: t`Statistics`,
        icon: "query_stats",
        path: ApplicationPath.PimeoStatistics,
        title: t`Statistics`,
        withMenuItem: true,
      },
    ],
  }

  function getSpeakupPage(): NavigationTab[] {
    const pages: NavigationTab[] = [
      {
        component: DashboardPage,
        forceMenuLabel: t`SpeakUP`,
        icon: speakupLogo,
        isIconImage: true,
        path: ApplicationPath.SpeakUpDashboard,
        title: t`Dashboard`,
        withMenuItem: true,
      },
      {
        component: ScheduledSurveysPage,
        path: APPLICATION_URL.speakupScheduledSurveys(),
        title: t`Surveys`,
      },
    ]

    if (customer?.packages.some(pack => pack.codeName === PackageCode.CreateCustomSurvey && pack.isEnabled)) {
      pages.push({
        component: SurveysPage,
        isUnauthorized: !isUserAllowed(UserPermissionCode.SurveyCreate),
        path: APPLICATION_URL.speakupSurveys(),
        title: t`Create a survey`,
      })
      pages.push({
        component: QuestionsPage,
        path: APPLICATION_URL.speakupQuestions(),
        title: t`Create a question`,
      })
    }

    return pages
  }

  function getLeaPage(): NavigationTab[] {
    const pages: NavigationTab[] = []

    if (customer?.packages.some(
      pack => pack.codeName === PackageCode.Lea && pack.isEnabled,
    )
    ) {
      pages.push(
        {
          component: DashboardPage,
          forceMenuLabel: t`Lea`,
          icon: leaLogo,
          isIconImage: true,
          path: ApplicationPath.LeaDashboard,
          title: t`Dashboard`,
          withMenuItem: true,
        },
        {
          component: ScheduledSurveysPage,
          path: APPLICATION_URL.leaScheduledSurveys(),
          title: t`Surveys`,
        },
      )
    }

    return pages
  }

  function getLedPageForCustomer(customer: CustomerSession | undefined): NavigationTab[] {
    const ledPages: NavigationTab[] = []

    if (customer?.psyCompany) {
      ledPages.push(
        {
          component: LedStatisticsPage,
          forceMenuLabel: t`LED`,
          icon: "perm_phone_msg",
          path: ApplicationPath.LedStatistics,
          title: t`Statistics`,
          withMenuItem: true,
        },
        {
          component: LedKeyFactsPage,
          path: ApplicationPath.LedDashboard,
          title: t`Dashboard`,
        },
        {
          component: LedDiscussionsPage,
          path: ApplicationPath.LedDiscussions,
          title: t`Discussions`,
        },
      )
    }
    else {
      ledPages.push({
        component: LedKeyFactsPage,
        forceMenuLabel: t`LED`,
        icon: "perm_phone_msg",
        path: ApplicationPath.LedDashboard,
        title: t`Dashboard`,
        withMenuItem: true,
      })
    }

    return ledPages
  }

  return navigationTabs[feature]
}
